import {createSelector} from 'reselect';

import productHelpers from '@/services/product/productHelpers';
import {
    selectConsumerProducts,
    selectConsumerProductsInfo,
    selectConsumerProductsMergedInfo,
} from '@/state/selectors/consumer';
import {selectChargerCodentify, selectHolderCodentify} from '@/state/selectors/mw';

const selectDevice = (state) => state.device;

const selectDeviceCodentify = createSelector(selectDevice, (device) => device?.codentify);

const selectIsSelectedDeviceConnected = createSelector(
    selectChargerCodentify,
    selectDeviceCodentify,
    (connectedCodentifyId, selectedCodentifyId) => connectedCodentifyId === selectedCodentifyId
);

const selectDeviceData = createSelector(selectDevice, selectIsSelectedDeviceConnected, (device, isConnectedDevice) => {
    if (isConnectedDevice) return device?.connectedData?.device;

    return device?.data?.device;
});

const selectHolderData = createSelector(selectDevice, selectIsSelectedDeviceConnected, (device, isConnectedDevice) => {
    if (isConnectedDevice) return device?.connectedHolderData?.device;

    return device?.holderData?.device;
});

const selectDeviceFirmwareVersion = createSelector(selectDeviceData, (deviceData) => deviceData?.firmwareVersion);

const selectHolderFirmwareVersion = createSelector(selectHolderData, (holderData) => holderData?.firmwareVersion);

const selectDeviceSerialNumber = createSelector(selectDeviceData, (deviceData) => deviceData?.serialNumber);

const selectConnectedDeviceConsumerProductMergedInfo = createSelector(
    selectConsumerProducts,
    selectConsumerProductsInfo,
    selectHolderCodentify,
    selectChargerCodentify,
    (products, productsInfo, holderCodentify, chargerCodentify) => {
        if (!products?.length) return;

        const product = productHelpers.findProduct({products, codentify: chargerCodentify});

        if (!product) return;

        return productHelpers.mapDeviceData(product, products, productsInfo, holderCodentify, true);
    }
);

const selectCurrentProductInfo = createSelector(
    selectDeviceCodentify,
    selectConsumerProductsMergedInfo,
    selectConnectedDeviceConsumerProductMergedInfo,
    selectIsSelectedDeviceConnected,
    (codentify, products, connectedDeviceConsumerProductMergedInfo, isSelectedDeviceConnected) => {
        if (isSelectedDeviceConnected) return connectedDeviceConsumerProductMergedInfo;

        if (!codentify) return;
        if (!products) return;

        const product = products.find((product) => product.codentify === codentify);

        if (!product) return;

        return {
            ...product,
        };
    }
);

const selectDeviceType = createSelector(selectCurrentProductInfo, (product) => product?.type);

const selectIsDeviceDataLoading = createSelector(selectDevice, (device) => device?.isDataLoading);

const selectDeviceBatteryInfo = createSelector(selectDeviceData, (deviceData) => deviceData?.batteryInfo);
const selectDeviceBattery = createSelector(selectDeviceBatteryInfo, (batteryInfo) => batteryInfo?.batteryChargeUnified);

const selectDeviceBatteryVoltageFactor = createSelector(
    selectDeviceData,
    (deviceData) => deviceData?.conversionFactor?.batteryVoltageFactor
);

const selectIsHolderConnectedToIot = createSelector(selectDevice, (device) => device?.isHolderConnectedToIot);
const selectIsDeviceConnectedToIot = createSelector(selectDevice, (device) => device?.isDeviceConnectedToIot);

const selectIsHolderConnectingToIot = createSelector(selectDevice, (device) => device?.isHolderConnectingToIot);
const selectIsDeviceConnectingToIot = createSelector(selectDevice, (device) => device?.isDeviceConnectingToIot);
const selectIsConnectingToIot = createSelector(
    selectIsHolderConnectingToIot,
    selectIsDeviceConnectingToIot,
    (isHolderConnectingToIot, isDeviceConnectingToIot) => isHolderConnectingToIot || isDeviceConnectingToIot
);

const selectIsSelectedDeviceConnectedToIot = createSelector(
    selectIsSelectedDeviceConnected,
    selectIsDeviceConnectedToIot,
    (isSelectedDeviceConnected, isDeviceConnectedToIot) => isSelectedDeviceConnected && isDeviceConnectedToIot
);

const selectIsDeviceWebSocketDisconnected = createSelector(
    selectDevice,
    (device) => device?.isDeviceWebSocketDisconnected
);
const selectIsHolderWebSocketDisconnected = createSelector(
    selectDevice,
    (device) => device?.isHolderWebSocketDisconnected
);

export {
    selectCurrentProductInfo,
    selectDeviceBattery,
    selectDeviceBatteryInfo,
    selectDeviceBatteryVoltageFactor,
    selectDeviceCodentify,
    selectDeviceData,
    selectDeviceFirmwareVersion,
    selectDeviceSerialNumber,
    selectDeviceType,
    selectHolderData,
    selectHolderFirmwareVersion,
    selectIsConnectingToIot,
    selectIsDeviceConnectedToIot,
    selectIsDeviceConnectingToIot,
    selectIsDeviceDataLoading,
    selectIsDeviceWebSocketDisconnected,
    selectIsHolderConnectedToIot,
    selectIsHolderConnectingToIot,
    selectIsHolderWebSocketDisconnected,
    selectIsSelectedDeviceConnected,
    selectIsSelectedDeviceConnectedToIot,
};
